import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { TreeSelect, Select, Spin } from 'antd';
import { SiteType } from '../graphql/types/SiteType';

export enum WarehouseDisplayMode {
    ALL = 'all',           // 展示所有，以树形结构展示，父级是hub
    HUB_ONLY = 'hub',      // 只有hub类型数据
    WAREHOUSE_ONLY = 'warehouse'  // 只有warehouse类型数据
}

interface WarehouseData {
    id?: string | number;
    name?: string;
    siteType?: number;
    parentSiteId?: string | number;
    [key: string]: any; // 允许其他字段
}

interface WarehouseSelectorProps {
    onWarehouseChange: (warehouseId: string, siteType: number) => void;
    displayMode?: WarehouseDisplayMode;  // 展示模式，默认为ALL
    warehouses: WarehouseData[];  // 外部传入的仓库数据
    loading?: boolean;  // 加载状态
    error?: any;  // 错误状态
    selectedWarehouseId?: string;  // 当前选中的仓库ID
    useGlobalState?: boolean;  // 是否使用全局状态，默认为false
}

interface WarehouseOption {
    value: string;
    title: string;
    siteType: SiteType;
    parentSiteId?: string;
    isParent?: boolean;
    children?: WarehouseOption[];
}

// 按 displayMode 过滤出实际可选的仓库/hub，规则和下面 treeData 的构建逻辑保持一致——
// 调用方用同一个函数做"默认选中第一个"之类的兜底逻辑，避免选中值和下拉框选项不一致
export const filterWarehousesForDisplayMode = (
    warehouses: WarehouseData[],
    displayMode: WarehouseDisplayMode
): WarehouseData[] => {
    switch (displayMode) {
        case WarehouseDisplayMode.HUB_ONLY:
            return warehouses.filter(w => w.siteType === SiteType.HUB);
        case WarehouseDisplayMode.WAREHOUSE_ONLY:
            return warehouses.filter(w => w.siteType === SiteType.WAREHOUSE);
        case WarehouseDisplayMode.ALL:
        default:
            // ALL 模式下不要求 warehouse 的上级 hub 在同一批数据里存在——
            // 账号可能只被授权到某个 warehouse 而未授权其上级 hub（孤儿 warehouse），
            // 这类条目交给 treeData 构建逻辑作为独立顶层节点展示，这里不能整体过滤掉
            return warehouses.filter(w => w.siteType === SiteType.HUB || w.siteType === SiteType.WAREHOUSE);
    }
};

const WarehouseSelector: React.FC<WarehouseSelectorProps> = ({
    onWarehouseChange,
    displayMode = WarehouseDisplayMode.ALL,
    warehouses,
    loading = false,
    error,
    selectedWarehouseId,
    useGlobalState = false
}) => {
    const [selectedWarehouse, setSelectedWarehouse] = useState<string>(selectedWarehouseId || '');

    // 同步外部传入的selectedWarehouseId
    useEffect(() => {
        if (selectedWarehouseId && selectedWarehouseId !== selectedWarehouse) {
            setSelectedWarehouse(selectedWarehouseId);
        }
    }, [selectedWarehouseId]);

    // 根据displayMode构建树形选项
    const treeData = useMemo(() => {
        console.log('🏢 WmsWarehouseSelector构建选项:', { 
            warehouses: warehouses?.length, 
            displayMode, 
            selectedWarehouseId 
        });
        if (!warehouses || warehouses.length === 0) return [];
        const filteredWarehouses = filterWarehousesForDisplayMode(warehouses, displayMode);
        const options: WarehouseOption[] = [];

        switch (displayMode) {
            case WarehouseDisplayMode.ALL:
                const hubs = filteredWarehouses.filter(w => w.siteType === SiteType.HUB);
                const warehouses_list = filteredWarehouses.filter(w => w.siteType === SiteType.WAREHOUSE);
                const hubIds = new Set(hubs.map(h => h.id?.toString()));
                hubs.forEach(hub => {
                    const childWarehouses = warehouses_list.filter(w => w.parentSiteId?.toString() === hub.id?.toString());
                    options.push({
                        value: hub.id?.toString() || '',
                        title: hub.name || '',
                        siteType: hub.siteType || SiteType.HUB,
                        isParent: true,
                        children: childWarehouses.map(warehouse => ({
                            value: warehouse.id?.toString() || '',
                            title: warehouse.name || '',
                            siteType: warehouse.siteType || SiteType.WAREHOUSE,
                            parentSiteId: warehouse.parentSiteId?.toString()
                        }))
                    });
                });
                // 孤儿 warehouse：上级 hub 不在当前账号可操作的仓库列表里，作为独立顶层节点展示，避免被整体丢弃
                const orphanWarehouses = warehouses_list.filter(w => !hubIds.has(w.parentSiteId?.toString()));
                orphanWarehouses.forEach(warehouse => {
                    options.push({
                        value: warehouse.id?.toString() || '',
                        title: warehouse.name || '',
                        siteType: warehouse.siteType || SiteType.WAREHOUSE,
                        parentSiteId: warehouse.parentSiteId?.toString()
                    });
                });
                break;
            case WarehouseDisplayMode.HUB_ONLY:
                filteredWarehouses.forEach(hub => {
                    options.push({
                        value: hub.id?.toString() || '',
                        title: hub.name || '',
                        siteType: hub.siteType || SiteType.HUB
                    });
                });
                break;
            case WarehouseDisplayMode.WAREHOUSE_ONLY:
                filteredWarehouses.forEach(warehouse => {
                    options.push({
                        value: warehouse.id?.toString() || '',
                        title: warehouse.name || '',
                        siteType: warehouse.siteType || SiteType.WAREHOUSE,
                        parentSiteId: warehouse.parentSiteId?.toString()
                    });
                });
                break;
        }

        return options;
    }, [warehouses, displayMode]);

    // 在树形数据中查找选项的辅助函数
    const findOptionInTree = (tree: WarehouseOption[], value: string): WarehouseOption | null => {
        for (const option of tree) {
            if (option.value === value) return option;
            if (option.children) {
                const found = findOptionInTree(option.children, value);
                if (found) return found;
            }
        }
        return null;
    };

    const handleChange = useCallback((value: string) => {
        setSelectedWarehouse(value);
        
        const selectedOption = findOptionInTree(treeData, value);
        if (selectedOption) {
            onWarehouseChange(value, selectedOption.siteType);
        }
    }, [treeData, onWarehouseChange]);


    if (displayMode === WarehouseDisplayMode.ALL) {
        return (
            <div className="w-full">
                <TreeSelect
                    value={selectedWarehouse}
                    onChange={handleChange}
                    treeData={treeData}
                    className="w-full"
                    showSearch
                    treeNodeFilterProp="title"
                    treeDefaultExpandAll
                    dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                    dropdownRender={(menu) => (
                        <div>
                            <style>{`
                                .ant-select-tree-indent-unit {
                                    width: 12px !important;
                                }
                                .ant-select-tree-node-content-wrapper {
                                    white-space: nowrap !important;
                                    overflow: hidden !important;
                                    text-overflow: ellipsis !important;
                                    flex: 1 !important;
                                    min-width: 0 !important;
                                }
                                .ant-select-tree-title {
                                    white-space: nowrap !important;
                                    overflow: hidden !important;
                                    text-overflow: ellipsis !important;
                                }
                                .ant-select-tree-treenode {
                                    display: flex !important;
                                    align-items: center !important;
                                }
                                .ant-select-tree-child-tree .ant-select-tree-treenode {
                                    padding-left: 4px !important;
                                }
                            `}</style>
                            {menu}
                        </div>
                    )}
                />
            </div>
        );
    } else {
        const selectOptions = treeData.map(option => ({
            value: option.value,
            label: option.title
        }));
        return (
            <div className="w-full">
                <Select
                    value={selectedWarehouse}
                    onChange={handleChange}
                    options={selectOptions}
                    className="w-full"
                    showSearch
                    filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                />
            </div>
        );
    }
};

export default WarehouseSelector;